import React, { useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Play, X, ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import AutoPlay from 'embla-carousel-autoplay';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/Button';

// Generate array of media items with video previews
const generateMediaItems = () => {
  const items = [];
  
  // Add images (1-48)
  for (let i = 1; i <= 48; i++) {
    const imageUrl = `https://rendezvouswebsite.b-cdn.net/experience%20carousel/Carousel%20Media%20(${i}).jpg`;
    items.push({
      type: 'image' as const,
      url: imageUrl,
      id: `image-${i}`
    });
  }

  // Add videos with their corresponding preview images
  const videoNumbers = [1, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31];
  for (const num of videoNumbers) {
    const videoUrl = `https://rendezvouswebsite.b-cdn.net/experience%20carousel/Carousel%20Media%20(${num}).mp4`;
    const previewUrl = `https://rendezvouswebsite.b-cdn.net/experience%20carousel/Carousel%20Media%20(${num}).jpg`;
    items.push({
      type: 'video' as const,
      url: videoUrl,
      previewUrl,
      id: `video-${num}`
    });
  }

  // Shuffle the array
  return items.sort(() => Math.random() - 0.5);
};

const allMedia = generateMediaItems();

export function MediaGallery() {
  const [selectedContent, setSelectedContent] = useState<(typeof allMedia)[0] | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [loadedVideos, setLoadedVideos] = useState<Set<string>>(new Set());
  const [failedMedia, setFailedMedia] = useState<Set<string>>(new Set());
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  
  // Initialize carousel with autoplay
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      dragFree: true,
      containScroll: 'trimSnaps',
      align: 'center',
      direction: 'ltr',
      skipSnaps: false
    },
    [AutoPlay({ delay: 4000, stopOnInteraction: false })]
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // Handle video loading success
  const handleVideoLoad = (id: string) => {
    setLoadedVideos(prev => new Set([...prev, id]));
  };

  // Handle media loading errors
  const handleMediaError = (id: string) => {
    setFailedMedia(prev => new Set([...prev, id]));
  };

  // Auto-play videos on hover
  React.useEffect(() => {
    if (hoveredIndex !== null) {
      const video = videoRefs.current[hoveredIndex];
      const item = allMedia[hoveredIndex];
      if (video && video.paused && item.type === 'video') {
        video.play().catch(() => {
          handleMediaError(item.id);
        });
      }
    }
  }, [hoveredIndex]);

  // Filter out failed media
  const validMedia = allMedia.filter(item => !failedMedia.has(item.id));

  return (
    <section className="py-12 md:py-24 bg-earth-900/95 relative overflow-hidden">
      <div className="absolute inset-0 bg-texture opacity-5" />
      
      <div className="relative overflow-hidden">
        {/* Navigation Buttons - Smaller on mobile */}
        <Button
          variant="secondary"
          size="sm"
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 bg-earth-900/80 hover:bg-earth-900 backdrop-blur-sm w-8 h-8 sm:w-12 sm:h-12 p-0"
          onClick={scrollPrev}
        >
          <ChevronLeft className="h-4 w-4 sm:h-6 sm:w-6" />
        </Button>

        <Button
          variant="secondary"
          size="sm"
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 bg-earth-900/80 hover:bg-earth-900 backdrop-blur-sm w-8 h-8 sm:w-12 sm:h-12 p-0"
          onClick={scrollNext}
        >
          <ChevronRight className="h-4 w-4 sm:h-6 sm:w-6" />
        </Button>

        <div className="embla" ref={emblaRef}>
          <div className="flex">
            {validMedia.map((item, index) => (
              <motion.div
                key={item.id}
                className="flex-[0_0_100%] sm:flex-[0_0_33.333%] md:flex-[0_0_25%] px-2 relative aspect-square overflow-hidden rounded-lg cursor-pointer group"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                onHoverStart={() => setHoveredIndex(index)}
                onHoverEnd={() => setHoveredIndex(null)}
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedContent(item)}
              >
                {item.type === 'video' ? (
                  <>
                    <img
                      src={item.previewUrl}
                      alt="Video preview"
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                        loadedVideos.has(item.id) ? 'opacity-0' : 'opacity-100'
                      }`}
                      onError={() => handleMediaError(item.id)}
                    />
                    <video
                      ref={el => videoRefs.current[index] = el}
                      src={item.url}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                        loadedVideos.has(item.id) ? 'opacity-100' : 'opacity-0'
                      }`}
                      loop
                      muted
                      playsInline
                      onLoadedData={() => handleVideoLoad(item.id)}
                      onError={() => handleMediaError(item.id)}
                      preload="metadata"
                    />
                    {(!loadedVideos.has(item.id) || hoveredIndex !== index) && (
                      <div className="absolute inset-0 flex items-center justify-center bg-earth-900/50">
                        <Play className="h-8 w-8 md:h-12 md:w-12 text-sand-100" />
                      </div>
                    )}
                  </>
                ) : (
                  <img
                    src={item.url}
                    alt="Experience moment"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    onError={() => handleMediaError(item.id)}
                  />
                )}

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-earth-900 via-transparent to-transparent opacity-60" />

                {/* Click to View Overlay */}
                <div className="absolute inset-0 bg-earth-900/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-sand-100 text-sm tracking-wider">
                    {item.type === 'video' ? 'Play Video' : 'View Image'}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal View */}
      <Dialog.Root open={!!selectedContent} onOpenChange={(open) => !open && setSelectedContent(null)}>
        <Dialog.Portal>
          <Dialog.Overlay 
            className="fixed inset-0 bg-earth-900/95 backdrop-blur-sm z-50"
          />
          <Dialog.Content className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] w-[95%] max-w-6xl z-50">
            <div className="relative bg-earth-800 rounded-lg overflow-hidden">
              {/* Close Button */}
              <Dialog.Close asChild>
                <Button 
                  variant="secondary"
                  size="sm"
                  className="absolute top-4 right-4 z-10 bg-earth-900/80 hover:bg-earth-900 backdrop-blur-sm"
                >
                  <X className="h-5 w-5" />
                </Button>
              </Dialog.Close>

              {/* Content */}
              <div className="aspect-video relative">
                {selectedContent?.type === 'video' ? (
                  <video
                    src={selectedContent.url}
                    className="w-full h-full object-cover"
                    controls
                    autoPlay
                    loop
                    playsInline
                    poster={selectedContent.previewUrl}
                  />
                ) : (
                  <img
                    src={selectedContent?.url}
                    alt="Experience moment"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </section>
  );
}